import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useEventTracker } from '../../hooks';

interface PexipEngageInstance {
  addEventListener: (handler: (event: PexipEngageEvent) => void) => void;
  dispose: () => void;
  EVENT_STEP_SHOWN: string;
  EVENT_APPOINTMENT_CREATED: string;
  appointment?: {
    subjectId: string;
    officeId: string;
  };
}

interface PexipEngageEvent {
  detail: {
    type: string;
    payload?: {
      step?: string;
      subject?: {
        title: string;
      };
      office?: {
        title: string;
      };
    };
  };
}

interface PexipEngagePlugin {
  EVENT_INIT?: string;
}

declare global {
  interface Window {
    PexipEngage: {
      (action: string, options?: { element: HTMLElement }): Promise<PexipEngageInstance>;
      Plugin?: PexipEngagePlugin;
    };
  }
}

const SkedifyColorAdvice = () => {
  const { locale } = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const { trackCustomEvent } = useEventTracker();

  const pluginIdRef = useRef<number | null>(null);
  const instanceRef = useRef<PexipEngageInstance | null>(null);
  const plugin = usePexipEngagePlugin();

  const language = locale === 'fr' ? 'fr-BE' : locale === 'en' ? 'en-GB' : 'nl-BE';

  const createEventHandler = useCallback(
    (instance: PexipEngageInstance) => {
      return (event: PexipEngageEvent) => {
        // Check for step shown event
        if (event.detail?.type === instance.EVENT_STEP_SHOWN) {
          trackCustomEvent(
            'appointment_booking_service_interacted',
            {
              bookingEngine: 'Skedify',
              stepLabel: event.detail.payload?.step,
              appointmentSubject: event.detail.payload?.subject?.title,
              appointmentStore: event.detail.payload?.office?.title,
            },
            true,
          );
        }

        // Check for appointment created event
        if (event.detail?.type === instance.EVENT_APPOINTMENT_CREATED) {
          trackCustomEvent(
            'appointment_booking_service_confirmed',
            {
              bookingEngine: 'Skedify',
              appointmentSubject: instance.appointment?.subjectId,
              appointmentStore: instance.appointment?.officeId,
            },
            true,
          );
        }
      };
    },
    [trackCustomEvent],
  );

  const cleanupInstance = useCallback(() => {
    pluginIdRef.current = null;
    
    if (instanceRef.current) {
      try {
        instanceRef.current.dispose();
      } catch (error) {
        console.warn('Error disposing instance:', error);
      }
      instanceRef.current = null;
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!plugin || !containerRef.current) {
      return;
    }

    const id = Date.now();
    pluginIdRef.current = id;

    const result = window.PexipEngage('create', {
      element: containerRef.current,
    });

    result
      .then((instance: PexipEngageInstance) => {
        if (pluginIdRef.current !== id) {
          return;
        }

        instanceRef.current = instance;

        if (instance && typeof instance.addEventListener === 'function') {
          const eventHandler = createEventHandler(instance);
          instance.addEventListener(eventHandler);
        }
      })
      .catch((error: Error) => {
        console.error('Failed to initialize plugin:', error);
      });

    return () => {
      cleanupInstance();
      
      result.then((instance: PexipEngageInstance) => {
        try {
          if (instance && instance.dispose) {
            instance.dispose();
          }
        } catch (error) {
          console.warn('Error disposing instance in fallback:', error);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugin, language]);

  return (
    <div
      className="pexip-engage-plugin relative min-h-[500px] w-full"
      data-pexip-auto-init="false"
      data-pexip-customer-timezone="Europe/Brussels"
      data-pexip-language={language}
      data-pexip-search_countries="BE,NL"
      data-pexip-version="1.0.0"
      key={`skedify-container-${language}`}
      ref={containerRef}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
          <div>Loading appointment scheduler...</div>
        </div>
      </div>
    </div>
  );
};

export default SkedifyColorAdvice;

const IS_SERVER = typeof window === 'undefined' || typeof document === 'undefined';

// eslint-disable-next-line sonarjs/cognitive-complexity
function usePexipEngagePlugin() {
  const [plugin, setPlugin] = useState<PexipEngagePlugin | undefined>(() =>
    IS_SERVER ? undefined : window.PexipEngage?.Plugin,
  );

  useEffect(() => {
    if (IS_SERVER) {
      return;
    }

    if (window.PexipEngage?.Plugin) {
      setPlugin(() => window.PexipEngage.Plugin);
      return;
    }

    const initializePlugin = () => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializePlugin);
        return;
      }

      const skedifyProjectUrl = process.env.NEXT_PUBLIC_SKEDIFY_PROJECT_HOST || 'plugin.pexipengage.com';
      const src = `https://${skedifyProjectUrl}/colora/pexip-engage-plugin.js`;

      if (!window.PexipEngage) {
        const script = document.createElement('script');
        script.innerHTML = `(function(n,i,c){if(typeof i>"u")return;let e="PexipEngage",o="script";n[e]=n[e]||function(){n[e].q=n[e].q||[];let s=arguments;if(s[0]==="init"){if(n[e].loaded)return;let r=i.getElementsByTagName(o)[0],t=i.createElement(o);t.defer=!0,t.async=!0,t.src=c,t.nonce=i.currentScript?.nonce,n[e].loaded=!0,r?.parentNode?.insertBefore(t,r);return}return new Promise((r,t)=>n[e].q.push([s,r,t]))};})(window,document,"${src}");window.PexipEngage("init");`;
        
        document.body.appendChild(script);
      }

      const checkPluginReady = () => {
        if (window.PexipEngage?.Plugin) {
          setPlugin(() => window.PexipEngage.Plugin);
        } else {
          requestAnimationFrame(checkPluginReady);
        }
      };
      
      requestAnimationFrame(checkPluginReady);
    };

    initializePlugin();
  }, []);

  return plugin;
}
